import React, { useRef, useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react'
import { bindActionCreators } from 'redux'
import isEqual from 'lodash/isEqual'
import ResizeHandler from 'components/utils/ResizeHandler'
import { html } from 'lib'
import { isEmbeddedReadOnly } from 'lib/embed'
import { getUsersForNode } from 'lib/node'
import { getUsersForEdge } from 'lib/edge'
import Node from 'components/graph/node'
import Edge from 'components/graph/edge'
// import Add from 'components/graph/controls/add' // hidden for now; replaced by CreateEdgeControl in the graph editing submenu
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { selectLoggedIn, selectMe } from 'selectors/auth'
import * as permissionsSelectors from 'selectors/permissions'
import { selectIsConfirming } from 'selectors/confirm'
import { requestContentGeneration, openContentGenerationModal } from 'actions/contentGeneration'
import { changePrompt } from 'actions/prompt'
import selectContentGeneration from 'selectors/contentGeneration'
import { selectShapes } from 'selectors/graph/shapes'
import { editOneNode } from 'actions/nodes'
import { editOneEdge } from 'actions/edges'
import { editOneKind, createOneKind } from 'actions/kinds'
import { getUsers } from 'actions/users'
import { setNewContentPermissions } from 'actions/permissions'
import ForceDirectedLayout from 'layout/force'
import {
  selectNeighborhood,
  selectDrag,
  selectEdit,
  selectObjectDetails,
  selectDepth,
  selectFocus,
  selectSelected,
  selectPinned,
  selectCreating,
  selectKinds,
} from 'selectors/graph'
import {
  startDragging,
  stopDragging,
  openObjectDetails,
  closeObjectDetails,
  startCreatingEdge,
  stopCreatingEdge,
  increaseDepth,
  decreaseDepth,
  resetDepth,
  focusOne,
  getNeighborhood,
  createNodeFromActive,
  createEdgesFromActive,
  createAndFocusNode,
  createEdge,
  createNode,
  deleteActive,
  togglePinActive,
  toggleDirectionalityActive,
  toggleSelectFocus,
  deselectAll,
} from 'actions/graph'
import type { RootState } from 'types/state'
import type {
  GraphEdge,
  GraphNode,
  GraphPinned,
  GraphSelected,
} from 'types/domain'
import type { ShapesAllMap } from 'types/shapes'
import 'containers/Graph.less'

type KeyEvent = {
  keyCode?: number
  preventDefault?: () => void
  target?: {
    tagName?: string
    isContentEditable?: boolean
  }
}

type PointerEventData = {
  pageX?: number
  pageY?: number
  clientX?: number
  clientY?: number
  changedTouches?: Array<{ pageX?: number; pageY?: number; clientX?: number; clientY?: number }>
  touches?: Array<{ pageX?: number; pageY?: number; clientX?: number; clientY?: number }>
  preventDefault?: () => void
  stopPropagation?: () => void
  cancelable?: boolean
  target?: {
    tagName?: string
    isContentEditable?: boolean
  }
}

const selectPermissions = permissionsSelectors.default || permissionsSelectors
const { selectPermissionsVisible, selectCanEditActiveEdges, selectCanEditActive } = permissionsSelectors

const toPointerEventData = (e: unknown): PointerEventData => e as PointerEventData

const toKeyEvent = (e: unknown): KeyEvent => e as KeyEvent

const toHtmlEvent = (e: unknown): Parameters<typeof html.getEventTarget>[0] => {
  const evt = toPointerEventData(e)
  const touch = evt.changedTouches?.[0] || evt.touches?.[0]
  const clientX = touch?.clientX ?? evt.clientX ?? 0
  const clientY = touch?.clientY ?? evt.clientY ?? 0
  if (touch) {
    return {
      clientX,
      clientY,
      changedTouches: [
        {
          clientX: touch.clientX ?? clientX,
          clientY: touch.clientY ?? clientY,
        },
      ],
    }
  }
  return { clientX, clientY }
}

interface GraphHandlers {
  onKeyDown: (e: Event) => void
  onMouseMove: (e: Event) => void
  onTouchMove: (e: Event) => void
  onMouseUp: (e: Event) => void
  onTouchEnd: (e: Event) => void
  onBlur: (e: Event) => void
}

const useGraphEventListeners = (handlers: GraphHandlers) => {
  const handlersRef = useRef(handlers)

  useEffect(() => {
    handlersRef.current = handlers
  })

  useEffect(() => {
    const wrap = (key: keyof GraphHandlers) => (e: Event) => handlersRef.current[key](e)
    const onKeyDown = wrap('onKeyDown')
    const onMouseMove = wrap('onMouseMove')
    const onTouchMove = wrap('onTouchMove')
    const onMouseUp = wrap('onMouseUp')
    const onTouchEnd = wrap('onTouchEnd')
    const onBlur = wrap('onBlur')

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('mouseup', onMouseUp)
    document.addEventListener('touchend', onTouchEnd, { passive: false })
    window.addEventListener('blur', onBlur)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('blur', onBlur)
    }
  }, [])
}

const Graph = ({ width, height }: { width?: number; height?: number }) => {
  const dispatch = useAppDispatch()

  // Selectors
  const graph = useAppSelector(selectNeighborhood)
  const kinds = useAppSelector(selectKinds.all)
  const drag = useAppSelector(selectDrag)
  const edit = useAppSelector(selectEdit)
  const objectDetails = useAppSelector(selectObjectDetails)
  const depth = useAppSelector(selectDepth)
  const focus = useAppSelector(selectFocus)
  const selected = useAppSelector(selectSelected)
  const pinned = useAppSelector(selectPinned)
  const creating = useAppSelector(selectCreating)
  useAppSelector((state) => selectLoggedIn(state).loggedIn)
  const me = useAppSelector((state) => selectMe(state).me)
  const isConfirming = useAppSelector(selectIsConfirming)
  const permissionsExpanded = useAppSelector((state) => selectPermissions(state).expanded)
  const permissionsVisible = useAppSelector(selectPermissionsVisible) ?? false
  const contentGeneration = useAppSelector((state) =>
    selectContentGeneration(state as RootState & { contentGeneration: unknown })
  ) as { showModal?: boolean } | undefined
  const shapes = useAppSelector(selectShapes)
  const editableActiveEdges = useAppSelector(selectCanEditActiveEdges)
  const editableActive = useAppSelector(selectCanEditActive)

  // Bound action creators
  const actions = useMemo(() => bindActionCreators({
    startDragging,
    stopDragging,
    openObjectDetails,
    closeObjectDetails,
    startCreatingEdge,
    stopCreatingEdge,
    focusOne,
    createEdgesFromActive,
    createNodeFromActive,
    createAndFocusNode,
    createEdge,
    createNode,
    editOneNode,
    editOneEdge,
    editOneKind,
    createOneKind,
    toggleSelectFocus,
    setNewContentPermissions,
    deleteActive,
    toggleDirectionalityActive,
    togglePinActive,
    deselectAll,
    getNeighborhood,
    getUsers,
    openContentGenerationModal,
    requestContentGeneration,
    changePrompt,
  }, dispatch), [dispatch])

  // Local state
  const [resized, setResized] = useState(false)
  const [mouse, setMouse] = useState({ x: 0, y: 0 })
  const [creatingDragTarget, setCreatingDragTarget] = useState<
    { type: 'node'; id: string } | { type: 'footer' } | { type: 'canvas' } | null
  >(null)
  const graphSizeRef = useRef<{ width: number; height: number } | null>(null)
  const wrapperSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 })

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const graphRef = useRef<HTMLDivElement>(null)
  const graphWrapperRef = useRef<HTMLDivElement>(null)
  const fadeOverlayRef = useRef<HTMLDivElement>(null)
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)

  const [canvasScale] = useState(() => {
    const s = window.devicePixelRatio || 1
    return s >= 2 ? s / 2 : s
  })

  // Layout (created once)
  const layoutRef = useRef<ForceDirectedLayout>(null!)
  if (!layoutRef.current) {
    layoutRef.current = new ForceDirectedLayout({
      increaseDepth: () => dispatch(increaseDepth()),
      decreaseDepth: () => dispatch(decreaseDepth()),
      resetDepth: () => dispatch(resetDepth()),
    })
    ;(window as unknown as { layout?: ForceDirectedLayout }).layout = layoutRef.current
  }
  const layout = layoutRef.current

  // Handlers
  const centerScroll = () => {
    const el = graphWrapperRef.current
    if (!el) return
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2
  }

  const updateGraphDimensions = useCallback((graphW: number, graphH: number) => {
    graphSizeRef.current = { width: graphW, height: graphH }
    if (canvasRef.current) {
      canvasRef.current.width = graphW * canvasScale
      canvasRef.current.height = graphH * canvasScale
    }
    if (graphRef.current) {
      graphRef.current.style.width = `${graphW}px`
      graphRef.current.style.height = `${graphH}px`
    }
    layout.setGraphDimensions(graphW, graphH, width, height)
  }, [canvasScale, layout, width, height])

  const onWrapperResize = useCallback((wrapperW?: number, wrapperH?: number) => {
    if (wrapperW == null || wrapperH == null) return
    wrapperSizeRef.current = { width: wrapperW, height: wrapperH }
    const maxW = wrapperW * 3
    const maxH = wrapperH * 3
    const cur = graphSizeRef.current
    const graphW = cur ? Math.min(Math.max(cur.width, wrapperW), maxW) : wrapperW
    const graphH = cur ? Math.min(Math.max(cur.height, wrapperH), maxH) : wrapperH
    updateGraphDimensions(graphW, graphH)
    if (!resized) {
      setResized(true)
      centerScroll()
    }
  }, [resized, updateGraphDimensions])

  const eventPosition = (e: unknown) => {
    const evt = toPointerEventData(e)
    const t = evt.changedTouches?.[0] || evt.touches?.[0] || evt
    let x = t.pageX ?? 0
    let y = t.pageY ?? 0
    if (graphWrapperRef.current) {
      x -= graphWrapperRef.current.offsetLeft
      y -= graphWrapperRef.current.offsetTop
      x += graphWrapperRef.current.scrollLeft
      y += graphWrapperRef.current.scrollTop
    }
    return { x, y }
  }

  const setMousePosition = (e: unknown) => {
    const pos = eventPosition(e)
    if (Number.isNaN(pos.x) || Number.isNaN(pos.y)) return
    layout.setMousePosition(pos.x, pos.y)
    setMouse({ x: pos.x, y: pos.y })

    if (!creating.isCreating) return
    const htmlEvent = toHtmlEvent(e)
    const node = html.getEventTarget(htmlEvent, (el: Element) => el.getAttribute('data-graph-type') === 'node')
    const footer = html.getEventTarget(htmlEvent, (el: Element) => el.getAttribute('data-graph-type') === 'footer')
    const next: NonNullable<typeof creatingDragTarget> = node
      ? { type: 'node', id: node.getAttribute('data-graph-id') || '' }
      : footer
        ? { type: 'footer' }
        : { type: 'canvas' }
    setCreatingDragTarget((prev) => (prev && isEqual(prev, next) ? prev : next))
  }

  // Clear the drop-target preview as soon as creation mode ends (drop, cancel, escape, etc).
  useEffect(() => {
    if (!creating.isCreating) setCreatingDragTarget(null)
  }, [creating.isCreating])

  const handleStopDragging = (e?: unknown) => {
    if (drag.isDragging) {
      const evt = toPointerEventData(e)
      if (evt?.cancelable) {
        evt.preventDefault?.()
        evt.stopPropagation?.()
      }
      actions.stopDragging()
    }
  }

  const onMouseDown = (e: unknown) => {
    ;(document.activeElement as HTMLElement | null)?.blur()
    setMousePosition(e)
    const evt = toPointerEventData(e)
    // Only preventDefault for mouse events (prevents text selection).
    // Touch events must NOT be prevented here — it cancels native scrolling.
    if (!edit.isEditing && evt.cancelable && !(e instanceof TouchEvent)) {
      evt.preventDefault?.()
    }
  }

  const onMouseUp = (e: unknown) => {
    setMousePosition(e)
    const evt = toPointerEventData(e)
    const htmlEvent = toHtmlEvent(e)
    const node = html.getEventTarget(htmlEvent, (el: Element) => el.getAttribute('data-graph-type') === 'node')
    const edge = html.getEventTarget(htmlEvent, (el: Element) => el.getAttribute('data-graph-type') === 'edge')
    const control = html.getEventTarget(htmlEvent, (el: Element) => el.getAttribute('data-graph-type') === 'control')
    const footer = html.getEventTarget(htmlEvent, (el: Element) => el.getAttribute('data-graph-type') === 'footer')
    const graphWrapper = html.getEventTarget(htmlEvent, (el: Element) => el === graphWrapperRef.current)
    if (objectDetails.isOpen && graphWrapper && !node && !edge && !control) {
      actions.closeObjectDetails(false)
    }
    if (!node && !edge) {
      handleStopDragging(e)
    }
    if (!creating.isCreating) return
    if (node) {
      const id = node.getAttribute('data-graph-id')
      if (id) actions.createEdgesFromActive({ to: id })
      actions.stopCreatingEdge()
      if (evt.cancelable) evt.preventDefault?.()
    } else if (footer) {
      // Dropping anywhere in the footer cancels creation instead of creating a new node.
      actions.stopCreatingEdge()
    } else if (!control) {
      const pos = eventPosition(e)
      actions.createNodeFromActive({
        position: { x: pos.x, y: pos.y },
      })
      actions.stopCreatingEdge()
      if (evt.cancelable) evt.preventDefault?.()
    }
  }

  const onKeyDown = (e: unknown) => {
    if (isEmbeddedReadOnly) return true
    const evt = toKeyEvent(e)
    if (contentGeneration?.showModal) return true
    const tag = evt.target?.tagName || ''
    const isTypingTarget = ['TEXTAREA', 'INPUT', 'SELECT'].includes(tag) || evt.target?.isContentEditable
    if (edit.isEditing || isConfirming || permissionsExpanded || isTypingTarget) return true
    actions.stopDragging()
    actions.stopCreatingEdge()
    switch (evt.keyCode) {
      case 13:
      case 10: // <enter>
        evt.preventDefault?.()
        if (objectDetails.isOpen) {
          actions.closeObjectDetails()
        } else {
          actions.openObjectDetails()
        }
        break
      case 78: // <n>
        actions.createNodeFromActive({ position: mouse })
        evt.preventDefault?.()
        break
      case 79: // <o>
        actions.createAndFocusNode()
        evt.preventDefault?.()
        break
      case 46:
      case 8: // <delete>
        if (!editableActive) return
        actions.deleteActive()
        evt.preventDefault?.()
        break
      case 68: // <d>
        if (!editableActiveEdges) return
        actions.toggleDirectionalityActive()
        evt.preventDefault?.()
        break
      case 32: // <space>
        actions.toggleSelectFocus()
        evt.preventDefault?.()
        break
      case 27: // <esc>
        actions.deselectAll()
        actions.closeObjectDetails()
        break
      case 80: // <p>
        actions.togglePinActive()
        evt.preventDefault?.()
        break
      case 84: // <t>
        actions.openContentGenerationModal()
        evt.preventDefault?.()
        break
      default:
        break
    }
  }

  // Global event listeners
  useGraphEventListeners({
    onKeyDown: onKeyDown as unknown as (e: Event) => void,
    onMouseMove: setMousePosition as unknown as (e: Event) => void,
    onTouchMove: setMousePosition as unknown as (e: Event) => void,
    onMouseUp: onMouseUp as unknown as (e: Event) => void,
    onTouchEnd: onMouseUp as unknown as (e: Event) => void,
    onBlur: handleStopDragging as unknown as (e: Event) => void,
  })

  // Register touchstart on graph element as non-passive so preventDefault works
  const onMouseDownRef = useRef(onMouseDown)
  onMouseDownRef.current = onMouseDown
  useEffect(() => {
    const el = graphRef.current
    if (!el) return
    const handler = (e: Event) => onMouseDownRef.current(e)
    el.addEventListener('touchstart', handler, { passive: false })
    return () => el.removeEventListener('touchstart', handler)
  }, [])

  // Update fade opacities based on scroll position (defined early so it can be used by zoom effect)
  const updateFades = useCallback(() => {
    const wrapper = graphWrapperRef.current
    const fade = fadeOverlayRef.current
    if (!wrapper || !fade) return

    const wrapperWidth = wrapper.clientWidth
    const wrapperHeight = wrapper.clientHeight
    const graphWidth = graphRef.current?.offsetWidth || wrapperWidth
    const graphHeight = graphRef.current?.offsetHeight || wrapperHeight
    const scrollLeft = wrapper.scrollLeft
    const scrollTop = wrapper.scrollTop

    // Calculate zoom ratio (max of both dimensions)
    const ratioX = graphWidth / wrapperWidth
    const ratioY = graphHeight / wrapperHeight
    const ratio = Math.max(ratioX, ratioY)

    // Calculate dynamic maxFade based on zoom level
    // At ratio 1.0: maxFade = 0 (no zoom)
    // At ratio 1.0+: maxFade = 0.1 + (ratio - 1) * 0.2, capped at 0.5
    let dynamicMaxFade = 0
    if (ratio > 1.0) {
      dynamicMaxFade = Math.min(0.1 + (ratio - 1) * 0.2, 0.5)
    }

    // Calculate max scrollable distances
    const maxScrollX = Math.max(0, graphWidth - wrapperWidth)
    const maxScrollY = Math.max(0, graphHeight - wrapperHeight)

    // Calculate opacity proportional to scroll position
    const leftOpacity = maxScrollX > 0 ? (scrollLeft / maxScrollX) * dynamicMaxFade : 0
    const rightRemaining = Math.max(0, graphWidth - (scrollLeft + wrapperWidth))
    const rightOpacity = maxScrollX > 0 ? (rightRemaining / maxScrollX) * dynamicMaxFade : 0
    const topOpacity = maxScrollY > 0 ? (scrollTop / maxScrollY) * dynamicMaxFade : 0
    const bottomRemaining = Math.max(0, graphHeight - (scrollTop + wrapperHeight))
    const bottomOpacity = maxScrollY > 0 ? (bottomRemaining / maxScrollY) * dynamicMaxFade : 0

    fade.style.setProperty('--left-fade', String(leftOpacity))
    fade.style.setProperty('--right-fade', String(rightOpacity))
    fade.style.setProperty('--top-fade', String(topOpacity))
    fade.style.setProperty('--bottom-fade', String(bottomOpacity))

    // Position overlay to match wrapper's viewport (fixed positioning)
    const rect = wrapper.getBoundingClientRect()
    fade.style.top = `${rect.top}px`
    fade.style.left = `${rect.left}px`
    fade.style.width = `${wrapperWidth}px`
    fade.style.height = `${wrapperHeight}px`
  }, [])

  // Zoom via wheel (ctrl+wheel / trackpad pinch) and touch pinch
  useEffect(() => {
    const el = graphWrapperRef.current
    if (!el) return

    const applyZoom = (zoomFactor: number, centerX: number, centerY: number) => {
      const wrapper = wrapperSizeRef.current
      if (wrapper.width === 0 || wrapper.height === 0) return
      const cur = graphSizeRef.current || { width: wrapper.width, height: wrapper.height }

      const newW = Math.min(Math.max(cur.width * zoomFactor, wrapper.width), wrapper.width * 3)
      const newH = Math.min(Math.max(cur.height * zoomFactor, wrapper.height), wrapper.height * 3)

      const rect = el.getBoundingClientRect()
      const cursorX = centerX - rect.left
      const cursorY = centerY - rect.top
      const oldScrollLeft = el.scrollLeft
      const oldScrollTop = el.scrollTop
      const contentX = oldScrollLeft + cursorX
      const contentY = oldScrollTop + cursorY
      const dw = newW - cur.width
      const dh = newH - cur.height
      const shiftX = dw * (contentX / cur.width)
      const shiftY = dh * (contentY / cur.height)

      graphSizeRef.current = { width: newW, height: newH }

      layout.queueZoom({
        newW, newH, shiftX, shiftY,
        windowWidth: width, windowHeight: height,
        onApplied: () => {
          if (canvasRef.current) {
            canvasRef.current.width = newW * canvasScale
            canvasRef.current.height = newH * canvasScale
          }
          if (graphRef.current) {
            graphRef.current.style.width = `${newW}px`
            graphRef.current.style.height = `${newH}px`
          }
          el.scrollLeft = oldScrollLeft + shiftX
          el.scrollTop = oldScrollTop + shiftY
          updateFades()
        }
      })
    }

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const zoomFactor = 1 - e.deltaY * 0.01
      applyZoom(zoomFactor, e.clientX, e.clientY)
    }

    let lastPinchDist = 0
    let pinchCenterX = 0
    let pinchCenterY = 0

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const t0 = e.touches[0]!
        const t1 = e.touches[1]!
        const dx = t0.clientX - t1.clientX
        const dy = t0.clientY - t1.clientY
        lastPinchDist = Math.sqrt(dx * dx + dy * dy)
        pinchCenterX = (t0.clientX + t1.clientX) / 2
        pinchCenterY = (t0.clientY + t1.clientY) / 2
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || lastPinchDist === 0) return
      e.preventDefault()
      const t0 = e.touches[0]!
      const t1 = e.touches[1]!
      const dx = t0.clientX - t1.clientX
      const dy = t0.clientY - t1.clientY
      const dist = Math.sqrt(dx * dx + dy * dy)
      const zoomFactor = dist / lastPinchDist
      pinchCenterX = (t0.clientX + t1.clientX) / 2
      pinchCenterY = (t0.clientY + t1.clientY) / 2
      applyZoom(zoomFactor, pinchCenterX, pinchCenterY)
      lastPinchDist = dist
    }

    const onTouchEnd = () => {
      lastPinchDist = 0
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [layout, canvasScale, width, height, updateFades])

  // Hook up updateFades to scroll and resize events
  useEffect(() => {
    const wrapper = graphWrapperRef.current
    if (!wrapper) return

    wrapper.addEventListener('scroll', updateFades)
    window.addEventListener('resize', updateFades)
    updateFades() // initial update
    return () => {
      wrapper.removeEventListener('scroll', updateFades)
      window.removeEventListener('resize', updateFades)
    }
  }, [updateFades])

  // Mount effect — useLayoutEffect matches componentDidMount timing:
  // runs synchronously after DOM mutations, before paint/requestAnimationFrame.
  // This ensures the canvas context is set before the layout simulation renders.
  useLayoutEffect(() => {
    if (focus.id) {
      actions.getNeighborhood({ focus: focus.id, selected, pinned, depth })
    }
    ctxRef.current = canvasRef.current?.getContext('2d') || null
    layout.setCanvasCtx(ctxRef.current, canvasScale)
    layout.setCurrentDepth(depth)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync shapes to layout
  const prevShapesRef = useRef<ShapesAllMap>({})
  useEffect(() => {
    if (isEqual(shapes, prevShapesRef.current)) return
    const prev = prevShapesRef.current
    prevShapesRef.current = shapes

    // Remove shapes that no longer exist or were toggled off
    for (const id of Object.keys(prev)) {
      if (!shapes[id] || !shapes[id].layout) {
        if (prev[id]?.layout) {
          layout.removeShape(id)
        }
      }
    }

    // Add or update shapes
    for (const [id, shape] of Object.entries(shapes)) {
      if (shape.layout) {
        layout.setShape(id, shape)
      }
    }
  }, [shapes, layout])

  // Update effects (componentDidUpdate equivalent)
  const prevProps = useRef<{
    focusId: string | undefined
    depth: number
    selected: GraphSelected
    pinned: GraphPinned
    creating: typeof creating
    drag: typeof drag
    width: number | undefined
    height: number | undefined
    nodeCount: number
    edgeCount: number
  } | null>(null)

  useLayoutEffect(() => {
    const nodeCount = Object.keys(graph.nodes).length
    const edgeCount = Object.keys(graph.edges).length
    const prev = prevProps.current

    if (prev !== null) {
      if (width !== prev.width || height !== prev.height) {
        const cur = graphSizeRef.current
        layout.setGraphDimensions(cur?.width ?? 0, cur?.height ?? 0, width, height)
      }
      if (nodeCount !== prev.nodeCount || edgeCount !== prev.edgeCount) {
        let userIds: string[] = []
        for (const n of Object.values(graph.nodes)) {
          userIds = userIds.concat(getUsersForNode(n as Parameters<typeof getUsersForNode>[0]))
        }
        for (const e of Object.values(graph.edges)) {
          userIds = userIds.concat(getUsersForEdge(e as Parameters<typeof getUsersForEdge>[0]))
        }
        actions.getUsers({ ids: userIds })
      }
      if (
        focus.id !== prev.focusId ||
        depth !== prev.depth ||
        !isEqual(selected, prev.selected) ||
        !isEqual(pinned, prev.pinned)
      ) {
        actions.getNeighborhood({ focus: focus.id, selected, pinned, depth })
      }
      if (!isEqual(creating, prev.creating)) {
        layout.setCreating(creating)
      }
      if (!isEqual(drag, prev.drag)) {
        layout.updateDragging(drag)
      }
      if (depth !== prev.depth) {
        layout.setCurrentDepth(depth)
      }
    }

    prevProps.current = {
      focusId: focus.id,
      depth,
      selected,
      pinned,
      creating,
      drag,
      width,
      height,
      nodeCount,
      edgeCount,
    }
  })

  const onResize = onWrapperResize

  // Render
  const style: React.CSSProperties = {}
  if (graphSizeRef.current) {
    style.width = `${graphSizeRef.current.width}px`
    style.height = `${graphSizeRef.current.height}px`
  }

  return (
    <div className="graph-wrapper" ref={graphWrapperRef}>
      <div className="graph-fade-overlay" ref={fadeOverlayRef} />
      <div
        className="graph"
        onMouseEnter={setMousePosition as unknown as React.MouseEventHandler}
        onMouseDown={onMouseDown as unknown as React.MouseEventHandler}
        ref={graphRef}
        style={style}
      >
        {Object.entries(graph.nodes).map(([id, n]: [string, GraphNode]) => {
          if (!resized) return null
          return (
            <Node
              key={`node-${id}`}
              object={n}
              kinds={kinds}
              editOneKind={actions.editOneKind}
              createOneKind={actions.createOneKind}
              layout={layout}
              permissionsVisible={permissionsVisible}
              toggleSelectFocus={actions.toggleSelectFocus}
              startDragging={actions.startDragging}
              stopDragging={actions.stopDragging}
              closeObjectDetails={actions.closeObjectDetails}
              openObjectDetails={actions.openObjectDetails}
              createEdgesFromActive={actions.createEdgesFromActive}
              stopCreatingEdge={actions.stopCreatingEdge}
              setNewContentPermissions={actions.setNewContentPermissions}
              isCreating={creating.isCreating}
              isDropTarget={creatingDragTarget?.type === 'node' && creatingDragTarget.id === id}
              edit={actions.editOneNode as any}
              editing={edit}
              focusOne={actions.focusOne}
              isDragging={drag.isDragging}
              updateMouse={setMousePosition}
              objectDetails={objectDetails}
              me={me}
            />
          )
        })}
        {Object.entries(graph.nodes).map(([id, n]: [string, GraphNode]) => {
          if (!resized) return null
          if (!n.more) return null
          return null
          return (
            <Node
              key={`node-${id}-more`}
              object={{
                id: `node-${id}-more`,
                modified: n.modified,
                depth: (n.depth || 0) + 2,
                isMore: true,
                isMoreFor: id,
                data: {
                  content: `${n.more} more...`,
                },
              }}
              kinds={kinds}
              editOneKind={actions.editOneKind}
              createOneKind={actions.createOneKind}
              layout={layout}
              permissionsVisible={permissionsVisible}
              toggleSelectFocus={actions.toggleSelectFocus}
              startDragging={actions.startDragging}
              stopDragging={actions.stopDragging}
              closeObjectDetails={actions.closeObjectDetails}
              openObjectDetails={actions.openObjectDetails}
              createEdgesFromActive={actions.createEdgesFromActive}
              stopCreatingEdge={actions.stopCreatingEdge}
              setNewContentPermissions={actions.setNewContentPermissions}
              isCreating={creating.isCreating}
              edit={actions.editOneNode as any}
              editing={edit}
              focusOne={actions.focusOne}
              isDragging={drag.isDragging}
              updateMouse={setMousePosition}
              objectDetails={objectDetails}
              me={me}
            />
          )
        })}
        {Object.entries(graph.nodes).map(([id, n]: [string, GraphNode]) => {
          if (!resized) return null
          if (!n.more) return null
          return null
          return (
            <Edge
              key={`edge-${id}-more`}
              object={{
                id: `edge-${id}-more`,
                from: n.id,
                to: `node-${id}-more`,
                modified: n.modified,
                depth: (n.depth || 0) + 2,
                isMore: true,
                data: {
                  content: '',
                },
              }}
              kinds={kinds}
              editOneKind={actions.editOneKind}
              createOneKind={actions.createOneKind}
              objectDetails={objectDetails}
              layout={layout}
              permissionsVisible={permissionsVisible}
              toggleSelectFocus={actions.toggleSelectFocus}
              startDragging={actions.startDragging}
              stopDragging={actions.stopDragging}
              closeObjectDetails={actions.closeObjectDetails}
              openObjectDetails={actions.openObjectDetails}
              setNewContentPermissions={actions.setNewContentPermissions}
              edit={actions.editOneEdge as any}
              editing={edit}
              focusOne={actions.focusOne}
              isDragging={drag.isDragging}
              updateMouse={setMousePosition}
              me={me}
            />
          )
        })}
        {/* Hidden for now: the per-node "+" drag-to-connect control is replaced by
            a control in the graph editing submenu (see Footer/GraphEditMenu/CreateEdgeControl).
            The `creating` state and its mouseup handling above are still used by that control.
        {Object.entries(graph.nodes).map(([id, n]: [string, GraphNode]) => {
          if (!resized) return null
          if (isEmbeddedReadOnly) return null
          if (edit.isEditing) return null
          if (!(n.focused || n.selected)) return null
          return (
            <Add
              key={`control-${id}`}
              layout={layout}
              node={n}
              isCreating={creating.isCreating}
              startCreating={actions.startCreatingEdge}
              stopCreating={actions.stopCreatingEdge}
              updateMouse={setMousePosition}
            />
          )
        })}
        */}
        {Object.entries(graph.edges).map(([id, e]: [string, GraphEdge]) => {
          if (!resized) return null
          return (
            <Edge
              key={`edge-${id}`}
              object={e}
              kinds={kinds}
              editOneKind={actions.editOneKind}
              createOneKind={actions.createOneKind}
              objectDetails={objectDetails}
              layout={layout}
              permissionsVisible={permissionsVisible}
              toggleSelectFocus={actions.toggleSelectFocus}
              startDragging={actions.startDragging}
              stopDragging={actions.stopDragging}
              closeObjectDetails={actions.closeObjectDetails}
              openObjectDetails={actions.openObjectDetails}
              setNewContentPermissions={actions.setNewContentPermissions}
              edit={actions.editOneEdge as any}
              editing={edit}
              focusOne={actions.focusOne}
              isDragging={drag.isDragging}
              updateMouse={setMousePosition}
              me={me}
            />
          )
        })}
        <ResizeHandler targetRef={graphWrapperRef} onResize={onResize} />
        <canvas ref={canvasRef} />
        {creatingDragTarget?.type === 'canvas' ? (
          <div
            className="new-node-preview"
            style={{ left: mouse.x, top: mouse.y }}
          />
        ) : null}
      </div>
    </div>
  )
}

export default Graph
